import { useCallback, useMemo, useEffect } from 'react'
import { useRouter } from 'next/router'
import { FLIGHT_FEATURE_FLAGS } from '@reward-platform/featureFlags'
import { SearchTabQuery, SearchTabQuerySchema } from '~/schemas/searchPageParams'
import { useFeatureFlag } from '~/context/featureFlag'
import { usePlatformRoutes } from '~/context/platform'
import { useIsSearchPageEnabled } from '~/components/search'

const FALLBACK_SLUG = SearchTabQuerySchema.enum.hotels

const useSearchPageRouter = (tabs?: SearchTabQuery[]) => {
  const router = useRouter()
  const { createRoutePath } = usePlatformRoutes()

  const flightsEnabled = useFeatureFlag(FLIGHT_FEATURE_FLAGS.flights)
  const carHireEnabled = useFeatureFlag('car-hire')
  const experiencesEnabled = useFeatureFlag('experiences')
  const enabledTabs = useMemo(
    () => ({
      [SearchTabQuerySchema.enum.hotels]: true,
      [SearchTabQuerySchema.enum.flights]: flightsEnabled,
      [SearchTabQuerySchema.enum.carhire]: carHireEnabled,
      [SearchTabQuerySchema.enum.experiences]: experiencesEnabled,
    }),
    [carHireEnabled, experiencesEnabled, flightsEnabled]
  )

  const isTabEnabled = useCallback((slug: SearchTabQuery) => enabledTabs[slug], [enabledTabs])

  const defaultTab = useMemo(
    () =>
      tabs?.find((tab) => {
        return isTabEnabled(tab)
      }),
    [isTabEnabled, tabs]
  )

  const validateTabSlug = useCallback(
    (slug?: string) => {
      const safeStringParse = SearchTabQuerySchema.safeParse(slug)
      if (safeStringParse.success && isTabEnabled(safeStringParse.data)) {
        return safeStringParse.data
      }
      return defaultTab ?? FALLBACK_SLUG
    },
    [defaultTab, isTabEnabled]
  )

  const selectedTab = useMemo(
    () => validateTabSlug(router.query.tab?.toString()),
    [router.query.tab, validateTabSlug]
  )

  const { isProductEnabled, getSlugFromQuery } = useIsSearchPageEnabled()
  const setSelectedTab = useCallback(
    (tabToSelect: string, includeQuery?: boolean) => {
      const tab = validateTabSlug(tabToSelect)
      const additionalQuery = includeQuery ? router.query : {}

      if (isProductEnabled(tab)) {
        const slug = getSlugFromQuery(tab)
        const route = {
          pathname: createRoutePath(`/${slug}`),
          query: additionalQuery,
        }
        router.push(route, route)
        return
      }

      const route = {
        pathname: createRoutePath(router.pathname),
        query: { ...additionalQuery, tab },
      }
      router.push(route, route, { shallow: true })
    },
    [createRoutePath, getSlugFromQuery, isProductEnabled, router, validateTabSlug]
  )

  useEffect(() => {
    // Updates url tab query
    if (defaultTab && router.query.tab !== selectedTab) {
      setSelectedTab(selectedTab, false)
    }
  }, [defaultTab, router.query.tab, selectedTab, setSelectedTab])

  return { selectedTab, setSelectedTab, isTabEnabled }
}

export default useSearchPageRouter
